import React, { useState } from 'react';
import { CheckCircle, Unpublished } from '@mui/icons-material';
import { List } from 'immutable';
import {
  Popover,
  Box,
  Typography,
  Paper
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { permit } from '@lba-dev/package.local-globals/restrictions';

import AudioExtractPlayer from './AudioExtractPlayer';

const styles = {
  tooltipPaper: {
    padding: 16,
    overflow: 'auto',
    borderRadius: 8
  },
  titleTypography: {
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: 8,
    marginBottom: 16
  },
  correctAnswerBox: {
    backgroundColor: '#e8f5e9',
    borderRadius: 4,
    padding: 12,
    marginBottom: 16
  },
  correctAnswerTypography: {
    fontWeight: 500,
    color: '#2e7d32'
  },
  extractsTitle: {
    marginTop: 8,
    marginBottom: 12
  },
  extractBox: {
    marginBottom: 20,
    padding: 12,
    borderLeft: '3px solid #1976d2',
    backgroundColor: '#ffffff',
    borderRadius: '0 4px 4px 0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  authorTypography: {
    marginBottom: 4,
    color: '#1976d2',
    fontWeight: 'bold'
  },
  extractText: {
    marginBottom: 8
  },
  timeBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  timeTypography: {
    color: 'rgba(0, 0, 0, 0.6)',
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
    paddingBottom: 4,
    borderRadius: 4,
    fontSize: '0.7rem'
  },
  clickableBox: {
    display: 'inline-flex',
    cursor: 'pointer'
  }
};

const AIControlIndicator = ({
  controleIA,
  question,
  classes,
  user
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  if (!controleIA || !controleIA.find(q => q.get('question') === question)) {
    return null;
  }

  const questionData = controleIA.find(q => q.get('question') === question);
  const isCorrect = questionData?.get('correct');
  const correctReponse = questionData?.get('correctReponse');
  const extraits = questionData?.get('extraits', new List());

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const tooltipContent = (
    <Popover
      open={open}
      onClose={handleTooltipClose}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          sx: {
            maxWidth: 350
          }
        }
      }}
    >
      <Paper elevation={3} className={classes.tooltipPaper}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          gutterBottom
          className={classes.titleTypography}
        >
          Analyse de l'IA
        </Typography>
        {!isCorrect && correctReponse && (
          <Box className={classes.correctAnswerBox}>
            <Typography
              variant="body2"
              className={classes.correctAnswerTypography}
            >
              <strong>Réponse correcte :</strong> {correctReponse}
            </Typography>
          </Box>
        )}
        {extraits.size > 0 && (
          <>
            <Typography
              variant="subtitle2"
              gutterBottom
              className={classes.extractsTitle}
            >
            Extraits utilisés :
            </Typography>
            {extraits.map((extrait) => (
              <Box
                key={`${extrait.get('start')}-${extrait.get('end')}`}
                className={classes.extractBox}
              >
                <Typography
                  variant="subtitle2"
                  className={classes.authorTypography}
                >
                  {extrait.get('author')}
                </Typography>

                <Typography variant="body2" className={classes.extractText}>
                  {extrait.get('text')}
                </Typography>
                <Box className={classes.timeBox}>
                  <Typography
                    variant="caption"
                    className={classes.timeTypography}
                  >
                    {Math.floor(extrait.get('start', 0))}s -
                    {Math.floor(extrait.get('end', 0))}s
                  </Typography>
                </Box>
                {extrait.get('url') && (
                  <AudioExtractPlayer
                    audioUrl={extrait.get('url')}
                    start={Number(extrait.get('start'))}
                    end={Number(extrait.get('end'))}
                  />
                )}
              </Box>
            ))}
          </>
        )}
      </Paper>
    </Popover>
  );

  const getVerificationIcon = (isCorrect) => {
    if (isCorrect) {
      return <CheckCircle color="success" fontSize="small" />;
    }

    if (correctReponse && correctReponse === 'Réponse douteuse ou ambigue') {
      return <Unpublished color="warning" fontSize="small" />;
    }

    return <Unpublished color="error" fontSize="small" />;
  };

  return (
    permit(user) ?
      <>
        <Box
          onClick={handleTooltipOpen}
          className={classes.clickableBox}
        >
          {getVerificationIcon(isCorrect)}
        </Box>
        {tooltipContent}
      </>
      : null
  );
};

export default withStyles(AIControlIndicator, styles);
